import React, { FC, useCallback, useEffect, useState } from 'react'
import { createUseStyles } from 'react-jss'
import { Overlay } from '../../ui/Sidebar'
import { Typography } from '../../ui/Typography/Typography'
import { Member } from '../../../models/Member'
import RadioButtons from '../../ui/FormElememts/RadioButton'
import { L, L_HTML } from '../../providers/Lokalise'
import { colors } from '../../../theme/palette'

import {
    contentWrapper,
    formControl,
    accordionWrapper,
    filterBottom,
} from '../../../theme/reuseables'
import { ChevronRight, Info } from '../../../assets/icons'
import { Card, CardCell, Circle } from '../../ui/Card'
import { CircleIcon, CircleText } from '../../ui/Circle/Circle'
import Checkbox from '../../ui/FormElememts/Checkbox'
import { Button } from '../../ui/Button/Button'
import { getInitials } from '../../../utils/initials'

const useStyles = createUseStyles({
    contentWrapper: {
        ...contentWrapper,
    },
    formControl: {
        ...formControl,
    },
    accordionWrapper: {
        ...accordionWrapper,
    },
    filterBottom: {
        ...filterBottom,
    },
    box: {
        display: 'flex',
        padding: '1rem .5rem',
        border: `1px solid ${colors.green}`,
        borderRadius: '0.5rem',
        flexDirection: 'column',
        marginTop: '2rem',
        marginBottom: '2rem',
        color: colors.black,
    },
    boxHeader: {
        display: 'flex',
        alignItems: 'center',
    },
    hr: {
        border: `.5px solid ${colors.green}`,
        marginBottom: '1rem',
        marginTop: '1rem',
        width: '100%',
    },
    linkButton: {
        marginRight: '.2rem',
        textDecoration: 'underline',
        color: colors.green,
        fontWeight: 'bold',
        cursor: 'pointer',
        '&:hover': {
            color: colors.red500,
        },
    },
    iconWrapper: {
        position: 'relative',
    },
    memberCard: {
        marginBottom: '8px',
    },
    letterHeader: {
        margin: '20px 0 10px',
        color: colors.green500,
    },
})

export enum ContactFlowState {
    INITIAL = 'initial',
    MEMBERS_SELECTED = 'members-selected',
    MEMBERS_SELECTED_INVALID = 'members-selected-invalid',
    SUCCESS = 'success',
    ERROR = 'error',
}

type ContactFlowProps = {
    isOpen: boolean
    onClose: () => void
    onSendDone: () => void
    chosenMembers: Member[]
}

const ContactFlow: FC<ContactFlowProps> = ({
    isOpen,
    onClose,
    onSendDone,
    chosenMembers,
}): JSX.Element => {
    const [flowState, setFlowState] = useState<ContactFlowState>(
        ContactFlowState.INITIAL
    )
    const [emailType, setEmailType] = useState<'arbejdsmail' | 'privatmail'>(
        'arbejdsmail'
    )
    const [sendToAll, setSendToAll] = useState<boolean>(false)
    const [validPrivateEmails, setValidPrivateEmails] = useState<Member[]>([])
    const [validWorkingEmails, setValidWorkingEmails] = useState<Member[]>([])
    const [noValidEmails, setNoValidEmails] = useState<Member[]>([])
    const [inValidPrivateEmails, setInValidPrivateEmails] = useState<Member[]>(
        []
    )
    const [inValidWorkingEmails, setInValidWorkingEmails] = useState<Member[]>(
        []
    )
    const classes = useStyles()

    /*
     * Check how many members have a valid email address
     * and how many have arbejdsmail or privatmail
     */
    const checkValids = useCallback(() => {
        if (chosenMembers) {
            // Reset all states first
            setValidWorkingEmails([])
            setValidPrivateEmails([])
            setInValidWorkingEmails([])
            setInValidPrivateEmails([])
            setNoValidEmails([])

            // Find members that have no valid emails
            const invalids = chosenMembers.filter(
                (member) =>
                    (!member?.workContact?.email ||
                        member?.workContact?.email === '') &&
                    (!member?.privateContact?.email ||
                        member?.privateContact?.email === '')
            )
            setNoValidEmails(invalids)

            // Valids are emails that are not in invalids
            const valids = chosenMembers.filter(
                (member) => !invalids.includes(member)
            )

            // Filter working emails
            const workingEmails = valids.filter((member) => {
                const email =
                    member?.workContact?.email !== '' &&
                    member?.workContact?.email
                return email && email.length > 0
            })

            // Filter private emails
            const privateEmails = valids.filter((member) => {
                const email =
                    member?.privateContact?.email !== '' &&
                    member?.privateContact?.email
                return email && email.length > 0
            })

            setValidWorkingEmails(workingEmails)
            setValidPrivateEmails(privateEmails)
            setInValidWorkingEmails(
                valids.filter((m) => !workingEmails.includes(m))
            )
            setInValidPrivateEmails(
                valids.filter((m) => !privateEmails.includes(m))
            )
        }
    }, [chosenMembers])

    const handleFlowState = (state: ContactFlowState): void => {
        setFlowState(state)
    }

    const sendEmail = (): void => {
        const mailingListRaw = []
        if (emailType === 'arbejdsmail') {
            // Get all valid working emails
            mailingListRaw.push(
                ...validWorkingEmails.map((member) => member.workContact?.email)
            )
            // if sendToAll is true, add all invalid working emails
            if (sendToAll) {
                mailingListRaw.push(
                    ...inValidWorkingEmails.map(
                        (member) => member.privateContact?.email
                    )
                )
            }
            // if sendToAll is true, add all invalid working emails
        } else {
            // Get all valid private emails
            mailingListRaw.push(
                ...validPrivateEmails.map(
                    (member) => member.privateContact?.email
                )
            )
            // if sendToAll is true, add all invalid private emails
            if (sendToAll) {
                mailingListRaw.push(
                    ...inValidPrivateEmails.map(
                        (member) => member.workContact?.email
                    )
                )
            }
        }
        const mailingListString =
            window.navigator.platform.includes('Win') ||
            window.navigator.userAgent.includes('Win')
                ? mailingListRaw.join(';')
                : mailingListRaw.join(',')
        window.open(`mailto:?bcc=${mailingListString}`, '_blank')
        onClose()
        onSendDone()
        setFlowState(ContactFlowState.INITIAL)

        // Reset the flow state
        setFlowState(ContactFlowState.INITIAL)
        setEmailType('arbejdsmail')
        setSendToAll(false)
    }

    useEffect(() => {
        checkValids()
    }, [chosenMembers])

    const renderInitialState = (): JSX.Element => {
        return (
            <>
                <Typography
                    tag="h2"
                    size="XL"
                    align="center"
                    weight="bold"
                    style={{ margin: '20px 0 10px' }}
                >
                    {L('MINEMEDLEMMER_KONTAKTFLERE_DIALOG_OVERSKRIFT_1')}
                </Typography>
                <Typography
                    tag="h3"
                    size="XS"
                    align="center"
                    weight="bold"
                    style={{ margin: '30px 0 -20px', paddingBottom: '0' }}
                >
                    {L_HTML('MINEMEDLEMMER_KONTAKTFLERE_SENDTIL_LABEL', {
                        antal: String(chosenMembers.length),
                    })}
                </Typography>
                <div className={classes.contentWrapper}>
                    <div
                        style={{
                            display: 'flex',
                            flexDirection: 'column',
                            gap: '1rem',
                            color: colors.black,
                        }}
                    >
                        <RadioButtons
                            groupLabel=""
                            options={[
                                {
                                    label: L(
                                        'MINEMEDLEMMER_KONTAKTFLERE_ARBEJDSMAIL_LABEL'
                                    ),
                                    value: 'arbejdsmail',
                                },
                                {
                                    label: L(
                                        'MINEMEDLEMMER_KONTAKTFLERE_PRIVAT-MAIL_LABEL'
                                    ),
                                    value: 'privatmail',
                                },
                            ]}
                            onChange={(e) => {
                                setEmailType(
                                    e.currentTarget.value as
                                        | 'arbejdsmail'
                                        | 'privatmail'
                                )
                                setSendToAll(false)
                            }}
                            activeValue={emailType}
                        />
                    </div>

                    {((emailType === 'arbejdsmail' &&
                        inValidWorkingEmails.length) ||
                        (emailType === 'privatmail' &&
                            inValidPrivateEmails.length)) && (
                        <>
                            <hr
                                className={classes.hr}
                                style={{
                                    marginTop: '2rem',
                                }}
                            />
                            <div className={classes.box}>
                                <div className={classes.boxHeader}>
                                    <Circle marginRight=".5rem">
                                        <CircleIcon iconWidth="16px">
                                            <Info />
                                        </CircleIcon>
                                    </Circle>
                                    <div className={classes.linkButton}>
                                        <Button
                                            type="action"
                                            onClick={() => {
                                                setFlowState(
                                                    ContactFlowState.MEMBERS_SELECTED
                                                )
                                            }}
                                        >
                                            <Typography
                                                tag="h3"
                                                size="XS"
                                                spacing=".2rem"
                                            >
                                                {emailType === 'arbejdsmail'
                                                    ? inValidWorkingEmails.length
                                                    : inValidPrivateEmails.length}
                                                {emailType === 'arbejdsmail'
                                                    ? inValidWorkingEmails.length >
                                                      1
                                                        ? ' medlemmer'
                                                        : ' medlem'
                                                    : inValidPrivateEmails.length >
                                                      1
                                                    ? ' medlemmer'
                                                    : ' medlem'}
                                            </Typography>
                                        </Button>
                                    </div>
                                    <Typography
                                        tag="h3"
                                        size="XS"
                                        spacing=".2rem"
                                    >
                                        {emailType === 'arbejdsmail'
                                            ? 'har ingen arbejdsmail'
                                            : 'har ingen privatmail'}
                                    </Typography>
                                </div>
                                <div
                                    style={{
                                        display: 'flex',
                                        gap: '10px',
                                        justifyContent: 'space-between',
                                        alignItems: 'center',
                                        color: colors.black,
                                    }}
                                >
                                    <div
                                        style={{
                                            marginLeft: '1rem',
                                            paddingTop: '0.2rem',
                                        }}
                                    >
                                        {emailType === 'arbejdsmail'
                                            ? L(
                                                  'MINEMEDLEMMER_KONTAKTFLERE_SEND_TIL_ARBEJDSMAIL-MAIL_LABEL'
                                              )
                                            : L(
                                                  'MINEMEDLEMMER_KONTAKTFLERE_SEND_TIL_PRIVAT-MAIL_LABEL'
                                              )}
                                    </div>
                                    <Checkbox
                                        id="5679"
                                        label=""
                                        isChecked={sendToAll}
                                        onChange={(e) => {
                                            setSendToAll(e.target.checked)
                                        }}
                                    />
                                </div>
                            </div>
                        </>
                    )}
                    {noValidEmails.length && (
                        <div className={classes.box}>
                            <div className={classes.boxHeader}>
                                <Circle marginRight=".5rem">
                                    <CircleIcon iconWidth="16px">
                                        <Info />
                                    </CircleIcon>
                                </Circle>
                                <div className={classes.linkButton}>
                                    <Button
                                        type="action"
                                        onClick={() => {
                                            setFlowState(
                                                ContactFlowState.MEMBERS_SELECTED_INVALID
                                            )
                                        }}
                                    >
                                        <Typography
                                            tag="h3"
                                            size="XS"
                                            spacing=".2rem"
                                        >
                                            {noValidEmails.length}
                                            {noValidEmails.length > 1
                                                ? ' medlemmer'
                                                : ' medlem'}
                                        </Typography>
                                    </Button>
                                </div>
                                <Typography tag="h3" size="XS" spacing=".2rem">
                                    {' '}
                                    {L(
                                        'MINEMEDLEMMER_KONTAKTFLERE_INGEN_VALIDE_MAILS_LABEL'
                                    )}
                                </Typography>
                            </div>
                        </div>
                    )}
                    <div
                        style={{
                            display: 'flex',
                            justifyContent: 'center',
                            marginTop: '3rem',
                            width: '100%',
                        }}
                    >
                        <Button
                            size="xlarge"
                            type="secondary"
                            style={{ flexGrow: 1 }}
                            onClick={() => {
                                onClose()
                            }}
                        >
                            {L('MINEMEDLEMMER_KONTAKTFLERE_DIALOG_LUK_KNAP')}
                        </Button>
                        <Button
                            size="xlarge"
                            style={{ flexGrow: 1 }}
                            onClick={() => {
                                sendEmail()
                            }}
                        >
                            Kontakt{' '}
                            {emailType === 'arbejdsmail'
                                ? `${
                                      validWorkingEmails.length +
                                      (sendToAll
                                          ? inValidWorkingEmails.length
                                          : 0)
                                  } ${
                                      validWorkingEmails.length +
                                          (sendToAll
                                              ? validWorkingEmails.length
                                              : 0) >
                                      1
                                          ? 'medlemmer'
                                          : 'medlem'
                                  }`
                                : `${
                                      validPrivateEmails.length +
                                      (sendToAll
                                          ? inValidPrivateEmails.length
                                          : 0)
                                  } ${
                                      inValidPrivateEmails.length +
                                          (sendToAll
                                              ? inValidPrivateEmails.length
                                              : 0) >
                                      1
                                          ? 'medlemmer'
                                          : 'medlem'
                                  }`}{' '}
                        </Button>
                    </div>
                </div>
            </>
        )
    }

    const renderMembersWithInvalidMails = (
        membersToshow: Member[]
    ): JSX.Element => {
        // Sort and group members by first letter of their name
        const groupedMembers = membersToshow.reduce((groups, member) => {
            const letter = member.fullName.charAt(0).toUpperCase()
            return {
                ...groups,
                [letter]: [...(groups[letter] || []), member],
            }
        }, {} as { [key: string]: Member[] })

        // Sort the letters
        const sortedLetters = Object.keys(groupedMembers).sort()

        return (
            <>
                <Typography
                    tag="h2"
                    size="XL"
                    align="center"
                    weight="bold"
                    style={{ margin: '20px 0 10px' }}
                >
                    <div
                        style={{
                            position: 'absolute',
                            left: '1rem',
                        }}
                    >
                        <Button
                            type="action"
                            onClick={() => {
                                setFlowState(ContactFlowState.INITIAL)
                            }}
                        >
                            <ChevronRight width="10px" color={colors.green} />
                        </Button>
                    </div>

                    {flowState === ContactFlowState.MEMBERS_SELECTED ? (
                        <>
                            {emailType === 'arbejdsmail'
                                ? 'Ingen arbejdsmail'
                                : 'Ingen privatmail'}
                        </>
                    ) : null}

                    {flowState === ContactFlowState.MEMBERS_SELECTED_INVALID &&
                        'Ingen valid email'}
                </Typography>

                <div
                    className={classes.contentWrapper}
                    style={{
                        backgroundColor: colors.green200,
                        overflow: 'auto',
                        height: '100%',
                    }}
                >
                    {sortedLetters.map((letter) => (
                        <div key={letter}>
                            <div className={classes.letterHeader}>
                                <Typography size="L" weight="bold">
                                    {letter}
                                </Typography>
                            </div>
                            {groupedMembers[letter].map((member) => (
                                <div
                                    className={classes.memberCard}
                                    key={`${member.memberId}-member`}
                                >
                                    <Card gridTemplateColumns="20% auto">
                                        <CardCell>
                                            <div
                                                className={classes.iconWrapper}
                                            >
                                                <Circle>
                                                    <CircleText>
                                                        {getInitials(
                                                            member.fullName
                                                        )}
                                                    </CircleText>
                                                </Circle>
                                            </div>
                                        </CardCell>
                                        <CardCell>
                                            <Typography size="XS">
                                                {member.fullName}
                                            </Typography>
                                            <Typography size="XXS">
                                                {member.primaryWorkPlace?.name}
                                            </Typography>
                                            <Typography size="XXS">
                                                {
                                                    member?.positions.find(
                                                        (position) =>
                                                            +position?.number ===
                                                            +member
                                                                .primaryWorkPlace
                                                                ?.postionNumber
                                                    )?.name
                                                }
                                            </Typography>
                                        </CardCell>
                                    </Card>
                                </div>
                            ))}
                        </div>
                    ))}
                </div>
            </>
        )
    }

    return (
        <>
            <Overlay
                isShowing={isOpen}
                hide={() => {
                    onClose()
                    setFlowState(ContactFlowState.INITIAL)
                }}
                useBackdropColor
            >
                <>
                    {flowState === ContactFlowState.INITIAL &&
                        renderInitialState()}
                    {flowState === ContactFlowState.MEMBERS_SELECTED &&
                        renderMembersWithInvalidMails(
                            emailType === 'arbejdsmail'
                                ? inValidWorkingEmails
                                : inValidPrivateEmails
                        )}
                    {flowState === ContactFlowState.MEMBERS_SELECTED_INVALID &&
                        renderMembersWithInvalidMails(inValidWorkingEmails)}
                </>
            </Overlay>
        </>
    )
}

export { ContactFlow }
